import { ArrowRight } from 'lucide-react'
import Link from 'next/link'
import { SeoReportSources } from '~/src/components/reports/SeoReportSources'
import type {
  HubSectionComponentProps,
  HubSourcesSummarySection,
} from './types'

export function HubSourcesSummary({
  section,
  context,
}: HubSectionComponentProps<HubSourcesSummarySection>) {
  const sourceSummary = context.sourceSummary
  const hasSourceLinks = Boolean(sourceSummary?.sourceLinks.length)
  const displayedTotal =
    sourceSummary?.sourcesReviewed ?? section.totalSources ?? null

  if (!hasSourceLinks && section.totalSources <= 0) return null

  if (hasSourceLinks && sourceSummary) {
    return (
      <section id={section.id} className='scroll-mt-14'>
        <SeoReportSources
          sources={sourceSummary.sourceLinks}
          productCount={sourceSummary.productCount}
          sourcesReviewed={displayedTotal}
          compact
        />
      </section>
    )
  }

  return (
    <section
      id={section.id}
      className='flex flex-col gap-3 rounded-2xl border border-border bg-accent p-5'
    >
      <div className='flex flex-wrap items-baseline gap-x-3 gap-y-1'>
        <h2 className='text-balance font-display text-2xl font-bold leading-8 text-foreground'>
          {section.title ?? 'Sources reviewed'}
        </h2>
        <span className='text-sm text-foreground-secondary'>
          {section.totalSources.toLocaleString('en-US')} sources
        </span>
      </div>

      {section.dek && (
        <p className='text-base leading-6 text-foreground-secondary'>
          {section.dek}
        </p>
      )}

      {section.breakdown && section.breakdown.length > 0 && (
        <ul className='flex flex-wrap gap-2'>
          {section.breakdown.map((entry) => (
            <li
              key={entry.kind}
              className='inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-xs font-medium text-foreground-secondary'
            >
              <span>{entry.kind}</span>
              <span className='text-foreground-tertiary'>{entry.count}</span>
            </li>
          ))}
        </ul>
      )}

      {section.href && (
        <Link
          href={section.href}
          className='inline-flex items-center gap-1 text-sm font-semibold text-info-700 hover:underline'
        >
          See all sources
          <ArrowRight className='size-3.5' aria-hidden />
        </Link>
      )}
    </section>
  )
}
